import { Modal, TabItem, Tabs, ModalProps, Avatar, Stack, Title } from "@skailer-com/uikit";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ScoreCardFeedbackAction } from "@/pages/recruit/candidates/components/ScoreCardFeedbackAction.tsx";
import { useApplicantShortCache } from "@/queries/recruit/use-applicants.query.ts";
import { useCandidatesByApplicant } from "@/queries/recruit/use-candidates.query.ts";
import { useCandidateFeedbacksOverview } from "@/queries/recruit/use-scorecards.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCandidateFeedback, ApiScoreCardStatus } from "@/types/recruit/scorecard.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

interface ScoreCardsFeedbacksModalProps extends ModalProps {
  candidateId: number;
  applicantId?: number;
}

export function ScoreCardsFeedbacksModal({
  candidateId,
  applicantId,
  ...props
}: ScoreCardsFeedbacksModalProps) {
  const { t } = useTranslation();
  const { isFeatureEnabled } = usePermissions();
  const [activeTabId, setActiveTabId] = useState<number>(-1);
  const { data: applicantShort } = useApplicantShortCache(applicantId);
  const { data: candidates } = useCandidatesByApplicant(applicantId);
  const { data: feedbacksOverview } = useCandidateFeedbacksOverview({
    candidateId: candidateId,
    enabled: !!candidateId && isFeatureEnabled(ApiFeatureType.ats_scorecards),
  });

  const currentCandidate = useMemo(() => {
    return candidates?.find((candidate) => candidate.id === candidateId);
  }, [candidates]);

  if (
    activeTabId != -1 &&
    !feedbacksOverview?.feedbacks.some((feedback) => feedback.scorecard.id === activeTabId)
  ) {
    setActiveTabId(-1);
  }

  const feedbacksGrouped = useMemo(() => {
    const groups: Record<string, ApiCandidateFeedback[]> = {
      completed: [],
      archived: [],
      others: [],
    };

    let feedbacks = feedbacksOverview?.feedbacks ?? [];

    if (activeTabId !== -1) {
      feedbacks = feedbacks.filter((feedback) => feedback.scorecard.id === activeTabId);
    }

    feedbacks.forEach((feedback) => {
      if (feedback.is_archived) {
        groups.archived.push(feedback);
      } else if (feedback.status === ApiScoreCardStatus.completed) {
        groups.completed.push(feedback);
      } else {
        groups.others.push(feedback);
      }
    });

    return groups;
  }, [feedbacksOverview, activeTabId]);

  const feedbackTabs = useMemo(() => {
    const tabs = [
      {
        title: "All",
        id: -1,
      },
    ];

    feedbacksOverview?.feedbacks.forEach((feedback) => {
      if (!tabs.find((tab) => tab.id === feedback.scorecard.id)) {
        tabs.push({
          id: feedback.scorecard.id,
          title: feedback.scorecard.title,
        });
      }
    });

    return tabs;
  }, [feedbacksOverview]);

  const activeTabIndex = useMemo(() => {
    return feedbackTabs.findIndex((tab) => tab.id === activeTabId);
  }, [feedbackTabs, activeTabId]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.scoreCards.resultsViewed, {
      [analyticProperties.id]: candidateId,
    });
  });

  return (
    <Modal
      {...props}
      layout={"stretch"}
      size={"md"}
      variant={"side"}
      withCloser
      closeByEscEnabled
      closeByOutsidePress
      title={t("scorecards:feedbacks")}
    >
      <Stack gap={"md"}>
        {applicantShort && (
          <Stack direction={"horizontal"} className={"items-center"} gap={"md"}>
            <Avatar size={"xl"} url={applicantShort?.photo_url} />
            <Stack>
              <Title
                header={`${applicantShort?.first_name} ${applicantShort?.last_name}`}
                size={"lg"}
                caption={currentCandidate?.pipeline_stage.title}
              />
            </Stack>
          </Stack>
        )}
        {feedbackTabs.length > 2 && (
          <Tabs
            activeTab={activeTabIndex}
            onActiveTabChange={(index) => {
              const selectedTab = feedbackTabs[index];
              setActiveTabId(selectedTab.id);
            }}
          >
            {feedbackTabs.map((tab) => (
              <TabItem key={tab.id} title={tab.title}></TabItem>
            ))}
          </Tabs>
        )}
        {feedbacksGrouped.completed.length > 0 && (
          <Stack gap={"md"}>
            {(feedbacksGrouped.archived.length > 0 || feedbacksGrouped.others.length > 0) && (
              <Title header={t("scorecards:completed")} size={"xs"} />
            )}
            {feedbacksGrouped.completed.map((feedback) => (
              <ScoreCardFeedbackAction
                feedback={feedback}
                dimmed={false}
                withEmployee
                key={`feedback_${feedback.id}`}
              />
            ))}
          </Stack>
        )}
        {feedbacksGrouped.others.length > 0 && (
          <Stack gap={"md"}>
            <Title header={t("common:status_invited")} paddingTop size={"xs"} />
            {feedbacksGrouped.others.map((feedback) => (
              <ScoreCardFeedbackAction
                feedback={feedback}
                dimmed={false}
                withEmployee
                key={`feedback_${feedback.id}`}
              />
            ))}
          </Stack>
        )}
        {feedbacksGrouped.archived.length > 0 && (
          <Stack gap={"md"}>
            <Title header={t("scorecards:deleted")} paddingTop size={"xs"} />
            {feedbacksGrouped.archived.map((feedback) => (
              <ScoreCardFeedbackAction
                feedback={feedback}
                dimmed={false}
                withEmployee
                key={`feedback_${feedback.id}`}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
